import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Download, Mail, Phone, FileText, Calendar, Search, Filter, Send, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * ADMIN VIEW - V5 TUTOR APPLICATIONS
 * 
 * Simple, dedicated admin panel for reviewing V5 tutor applications
 */
const AdminTutorApplicationsV5 = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [notifyingId, setNotifyingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  
  useEffect(() => {
    fetchApplications();
  }, []);
  
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/v5/tutor-applications`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      
      const data = await response.json();
      setApplications(data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadCV = (application) => {
    // Use the forced-download endpoint; this guarantees the file is saved
    // to the device even if the browser can't preview the MIME type
    // (common for mobile-uploaded CVs that arrive as application/octet-stream).
    const cvUrl = `${BACKEND_URL}${application.cv_url}?download=1`;
    const a = document.createElement('a');
    a.href = cvUrl;
    a.download = application.cv_original_name || 'cv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleNotifyTutor = async (application, { skipConfirm = false } = {}) => {
    const tutorName = application.full_name || 'this tutor';
    if (!skipConfirm) {
      const confirmed = window.confirm(
        `Send the welcome / acceptance email to ${tutorName} (${application.email})?`
      );
      if (!confirmed) return false;
    }

    try {
      setNotifyingId(application.id);
      const response = await fetch(
        `${BACKEND_URL}/api/v5/tutor-applications/${application.id}/notify`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send welcome email');
      }

      toast.success(`Welcome email sent to ${tutorName}`);

      // Optimistic local update so the UI reflects notified state immediately
      setApplications((prev) =>
        prev.map((app) =>
          app.id === application.id
            ? {
                ...app,
                notified: true,
                date_sent: data.date_sent,
                notified_date: data.notified_date || data.date_sent,
              }
            : app
        )
      );
      return true;
    } catch (error) {
      console.error('Notify tutor error:', error);
      toast.error(error.message || 'Failed to send welcome email');
      return false;
    } finally {
      setNotifyingId(null);
    }
  };

  const handleChangeApprovalStatus = async (application, newStatus) => {
    const currentStatus = (application.approval_status || application.status || 'pending').toLowerCase();
    if (currentStatus === newStatus) return; // no-op

    // Friendly confirmation for destructive transitions
    const labelMap = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
    const confirmed = window.confirm(
      `Change approval status for ${application.full_name} to "${labelMap[newStatus]}"?`
    );
    if (!confirmed) return;

    try {
      setStatusUpdatingId(application.id);

      const res = await fetch(
        `${BACKEND_URL}/api/v5/tutor-applications/${application.id}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ approval_status: newStatus }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update approval status');
      }

      // Update local state with merged server fields
      setApplications((prev) =>
        prev.map((app) =>
          app.id === application.id
            ? { ...app, ...(data.application || {}), approval_status: newStatus, status: newStatus }
            : app
        )
      );

      toast.success(`Status updated to ${labelMap[newStatus]}`);

      // Safe automation: only prompt for welcome email when moving to Approved
      // AND tutor has not yet been notified. Manual "Notify Tutor" button
      // is always available as a backup.
      if (newStatus === 'approved' && !application.notified) {
        const sendEmail = window.confirm(
          `Send welcome email to ${application.full_name} now?`
        );
        if (sendEmail) {
          // Pass the freshly-updated application object so optimistic
          // notification state stays consistent.
          const merged = { ...application, ...(data.application || {}) };
          await handleNotifyTutor(merged, { skipConfirm: true });
        }
      } else if (newStatus === 'approved' && application.notified) {
        // Duplicate prevention: inform admin instead of silently re-sending.
        toast.info('Welcome email was already sent earlier. Use "Notify Tutor" to resend manually.');
      }
    } catch (error) {
      console.error('Approval status update error:', error);
      toast.error(error.message || 'Failed to update approval status');
    } finally {
      setStatusUpdatingId(null);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const renderApprovalControl = (application) => {
    const current = (application.approval_status || application.status || 'pending').toLowerCase();
    const isUpdating = statusUpdatingId === application.id;
    const options = [
      { key: 'pending', label: 'Pending', active: 'bg-yellow-500 text-white border-yellow-500', idle: 'text-yellow-800 hover:bg-yellow-50' },
      { key: 'approved', label: 'Approved', active: 'bg-green-600 text-white border-green-600', idle: 'text-green-800 hover:bg-green-50' },
      { key: 'rejected', label: 'Rejected', active: 'bg-red-600 text-white border-red-600', idle: 'text-red-800 hover:bg-red-50' },
    ];

    return (
      <div
        className="inline-flex rounded-md border border-gray-200 overflow-hidden bg-white"
        role="group"
        aria-label="Approval status"
        data-testid={`approval-control-${application.id}`}
      >
        {options.map((opt) => {
          const isActive = current === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              disabled={isUpdating}
              onClick={() => handleChangeApprovalStatus(application, opt.key)}
              className={`px-3 py-1.5 text-xs font-medium border-r last:border-r-0 border-gray-200 transition-colors ${
                isActive ? opt.active : `bg-white ${opt.idle}`
              } ${isUpdating ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid={`approval-${opt.key}-btn-${application.id}`}
            >
              {isUpdating && isActive ? '…' : opt.label}
            </button>
          );
        })}
      </div>
    );
  };
  
  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.subjects?.toLowerCase().includes(searchTerm.toLowerCase());

    const effectiveStatus = (app.approval_status || app.status || 'pending').toLowerCase();
    const matchesStatus = filterStatus === 'all' || effectiveStatus === filterStatus;

    return matchesSearch && matchesStatus;
  });
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">V5 Tutor Applications</h1>
          <p className="text-gray-600">Review and manage tutor applications</p>
        </div>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, or subjects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('all')}
                  size="sm"
                >
                  All
                </Button>
                <Button
                  variant={filterStatus === 'pending' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('pending')}
                  size="sm"
                >
                  Pending
                </Button>
                <Button
                  variant={filterStatus === 'approved' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('approved')}
                  size="sm"
                >
                  Approved
                </Button>
                <Button
                  variant={filterStatus === 'rejected' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('rejected')}
                  size="sm"
                >
                  Rejected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Applications List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredApplications.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-500">No applications found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((application) => (
              <Card key={application.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{application.full_name}</CardTitle>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          <a href={`mailto:${application.email}`} className="hover:text-blue-600">
                            {application.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          <a href={`tel:${application.phone}`} className="hover:text-blue-600">
                            {application.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(application.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {renderApprovalControl(application)}
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Subjects:</p>
                      <p className="text-gray-900">{application.subjects}</p>
                    </div>
                    
                    {application.notes && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Additional Notes:</p>
                        <p className="text-gray-900 text-sm">{application.notes}</p>
                      </div>
                    )}
                    
                    <div className="pt-3 border-t flex flex-wrap items-center gap-2">
                      <Button
                        onClick={() => handleDownloadCV(application)}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        data-testid={`download-cv-btn-${application.id}`}
                      >
                        <Download className="h-4 w-4" />
                        Download CV ({application.cv_original_name})
                      </Button>

                      {application.notified ? (
                        <Badge
                          className="gap-1 bg-green-100 text-green-800 border border-green-200"
                          data-testid={`notified-badge-${application.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Tutor Notified
                          {application.date_sent && (
                            <span className="ml-1 text-green-700/80 font-normal">
                              · {formatDate(application.date_sent)}
                            </span>
                          )}
                        </Badge>
                      ) : (
                        <Button
                          onClick={() => handleNotifyTutor(application)}
                          disabled={notifyingId === application.id}
                          size="sm"
                          className="gap-2 bg-[#007A7A] hover:bg-[#006060] text-white"
                          data-testid={`notify-tutor-btn-${application.id}`}
                        >
                          {notifyingId === application.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Sending…
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              Notify Tutor
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTutorApplicationsV5;
